import { useGetPonds } from 'modules/pond/api/queries'
import { useShrimpSizeTableSelector } from 'modules/pond/api/selector'
import { useCallback, useEffect, useMemo } from 'react'

import { EmptyState } from './components/EmptyState'
import { PondBlock } from './components/PondBlock'
import { SkeletonTable } from './components/SkeletonTable'
import { AUTO_FILL_MAX_PAGES, MIN_VISIBLE_DATES } from './constants'
import { usePondBlocks } from './hooks/usePondBlocks'
import { tableStyles } from './styles'

type ShrimpSizeDashboardProps = {
  setLoadingMobile?: (isLoading: boolean) => void
}

// The all-ponds ไซส์กุ้ง table (NK-276), laid out per NK-281 as one collapsible
// block per pond instead of one tall matrix: each block shows อายุกุ้ง and ไซส์กุ้ง
// until opened, and only the dates that pond was measured on. The mobile app
// renders this same page in a WebView (ShrimpSizeTablePage), so it is the one
// implementation for both.
export default function ShrimpSizeDashboard({ setLoadingMobile }: ShrimpSizeDashboardProps) {
  const {
    data,
    isLoading,
    isFetching,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    pageCount,
  } = useShrimpSizeTableSelector()

  // The table payload identifies a pond only by name, so the บ่อลูก badge is
  // resolved from the ponds list the page has already loaded.
  const { data: ponds } = useGetPonds()
  const childPondNames = useMemo(
    () => new Set((ponds ?? []).filter((pond) => pond.isChild).map((pond) => pond.name)),
    [ponds]
  )

  const isMobile = new URLSearchParams(window.location.search).get('isMobile') === 'true'

  const allBlocks = usePondBlocks(data?.columns, data?.rows)
  // A pond with no ไซส์กุ้ง record at all gets no block, even with an open cycle.
  // Pages cover the farm's dates, so "none" means "none on the pages loaded so far";
  // a pond whose only records are older shows up once the page holding them loads.
  const blocks = useMemo(() => allBlocks.filter((block) => block.dates.length > 0), [allBlocks])

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetching) fetchNextPage()
  }, [hasNextPage, isFetching, fetchNextPage])

  // See MIN_VISIBLE_DATES: top up thin ponds right after the first load, within a
  // fixed number of pages. It stops on an error — hasNextPage survives a failed
  // page, so without that a backend that keeps failing is retried forever — and
  // waits out ANY fetch, not just a next-page one: fetchNextPage cancels a running
  // refetch of the cached pages (refetchOnMount), leaving stale pages under a fresh one.
  //
  // With every pond hidden there is nothing to scroll and no ← to press, so the cap
  // is lifted until a pond with a record appears or the pages run out.
  useEffect(() => {
    if (!hasNextPage || isFetching || isError) return
    if (blocks.length === 0) {
      fetchNextPage()
      return
    }
    if (pageCount >= AUTO_FILL_MAX_PAGES) return
    if (blocks.some((block) => block.dates.length < MIN_VISIBLE_DATES)) fetchNextPage()
  }, [blocks, hasNextPage, isFetching, isError, pageCount, fetchNextPage])

  useEffect(() => {
    if (isMobile && setLoadingMobile) setLoadingMobile(isLoading)
  }, [isMobile, isLoading, setLoadingMobile])

  if (isLoading || (blocks.length === 0 && hasNextPage && !isError)) return <SkeletonTable />

  if (blocks.length === 0) return <EmptyState />

  return (
    <div style={tableStyles.list(isMobile)}>
      {blocks.map((block) => (
        <PondBlock
          key={block.pond}
          block={block}
          isMobile={isMobile}
          isChildPond={childPondNames.has(block.pond)}
          hasNextPage={!!hasNextPage}
          isLoadingMore={isFetchingNextPage}
          onLoadMore={handleLoadMore}
        />
      ))}
    </div>
  )
}
